import type { EpkRead } from "../../app/hooks/useEpk.js";

/**
 * The EPK EDITOR's draft shape, the mapper that seeds it from a read, and the map of which section card owns
 * which field.
 *
 * It sits beside `EpkCard.tsx` rather than in it because the section map has to be readable from a test — the
 * partition is the thing being pinned — and a component module that also exports a constant loses fast
 * refresh for the whole file.
 */

export type QuoteDraft = { quote: string; source: string };
export type MomentDraft = { title: string; detail: string };
export type ContactDraft = { name: string; role: string; company: string; email: string };
export type Draft = {
  // No stats-snapshot UI (PO nixed the section 2026-08-16) — but stored pins ROUND-TRIP untouched
  // through every save (the old-mobile precedent: "the editor went, not stats._manual"). Sending
  // them back verbatim is load-bearing: the whole-document PUT would otherwise WIPE them.
  statPins: Record<string, number>;
  tagline: string;
  foundedYear: string;
  basedIn: string;
  forFansOf: string[];
  ffoHidden: boolean;
  videoEmbedUrl: string;
  spotifyEmbedUrls: string[];
  quotes: QuoteDraft[];
  moments: MomentDraft[];
  contacts: ContactDraft[];
  videoTitle: string;
};

export function toDraft(read: EpkRead): Draft {
  return {
    statPins: { ...(read.statPins ?? {}) },
    tagline: read.tagline ?? "",
    foundedYear: read.foundedYear != null ? String(read.foundedYear) : "",
    basedIn: read.basedIn ?? "",
    forFansOf: [...(read.forFansOf ?? [])],
    ffoHidden: read.ffoHidden ?? false,
    videoEmbedUrl: read.videoEmbedUrl ?? "",
    spotifyEmbedUrls: [...(read.spotifyEmbedUrls ?? [])],
    quotes: (read.quotes ?? []).map((qq) => ({ quote: qq.quote ?? "", source: qq.source ?? "" })),
    moments: (read.moments ?? []).map((m) => ({ title: m.title ?? "", detail: m.detail ?? "" })),
    contacts: (read.contacts ?? []).map((c) => ({
      name: c.name ?? "", role: c.role ?? "", company: c.company ?? "", email: c.email ?? "",
    })),
    videoTitle: read.videoTitle ?? "",
  };
}

/**
 * WHICH SECTION CARD OWNS WHICH DRAFT FIELD — the map behind the five Save buttons' dirty look.
 *
 * Every editable field belongs to exactly one section; `statPins` is deliberately in none, having no UI at
 * all (it round-trips untouched so the whole-document PUT cannot wipe it). `epkSectionsCoverTheDraft` in the
 * test file pins that partition: a field added to `Draft` with UI and no section here would edit fine, save
 * fine, and light no Save button — the failure nothing else can see.
 */
export const EPK_SECTIONS = {
  info: ["tagline", "foundedYear", "basedIn", "forFansOf", "ffoHidden"],
  media: ["videoEmbedUrl", "videoTitle", "spotifyEmbedUrls"],
  quotes: ["quotes"],
  contacts: ["contacts"],
  moments: ["moments"],
} as const satisfies Record<string, readonly (keyof Draft)[]>;
